import { Injectable } from '@angular/core';
import { ConfigurationsProvider } from '@modeso/aumico-lib-core-fe';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { Observable, concatMap, mergeMap, of } from 'rxjs';
import * as fromLicenceActions from '../actions/licence.actions';
import * as fromActions from '../actions/organization.actions';
import { GetOrganizationDto } from '../dtos/getOrganizationDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { referralDto } from '../dtos/referralDto.interface';
import { UpdateOrganizationDto } from '../dtos/updateOrganizationDto.interface';

import { LicenceStatusDto } from '../dtos/licenceStatusDto';
import { OrganizationSearchFilterDto, OrganizationSearchResultDto } from '../dtos/organizationSearchDto.interface';
import { CPMMState } from '../enums/cpmm-state.enum';
import { FCPState } from '../enums/subscription-state.enum';
import { OrganizationSettings } from '../interfaces/organization-settings.interface';
import { UpdateOrganizationAbcpDto } from '../interfaces/update-organization-abcp-request.interface';
import { UpdateOrganizationLocaleDto } from '../interfaces/update-organization-locale-request.interface';
import { IUpdateOrganizationState } from '../interfaces/update-organization-state-request.interface';
import { UpdateOrganizationTierDto } from '../interfaces/update-organization-tier-request.interface';
import { IUploadOrganizationReportingTemplateDto } from '../interfaces/upload-organization-reporting-template-dto.interface';
import * as fromFeature from '../reducers/organization.reducer';

const debug = Debug('aumico:aumico-lib-users-fe:UsersProvider');

@Injectable()
export class OrganizationProvider {
    constructor(protected store: Store<fromFeature.AppState>, protected configuration: ConfigurationsProvider) {
        debug('provider constructed');
    }

    dispatchGetCurrentOrganization(): void {
        this.store.dispatch(fromActions.getCurrentOrganization.do());
    }

    dispatchGetUser(userId: string): void {
        this.store.dispatch(fromActions.getUserById.do({ payload: userId }));
    }

    getCurrentOrganization$(): Observable<GetOrganizationDto> {
        return this.store.select(fromFeature.selectCurrentOrganization);
    }

    getCurrentOrganizationName$(): Observable<string> {
        return this.store.select(fromFeature.selectCurrentOrganizationName).pipe(
            mergeMap((organization) => {
                if (!organization) {
                    this.dispatchGetCurrentOrganization();
                    return of(organization).pipe(concatMap((result) => result));
                } else {
                    return of(organization);
                }
            }),
        );
    }

    getCurrentOrganizationAbv$(): Observable<string> {
        return this.store.select(fromFeature.selectCurrentOrganizationAbv);
    }

    dispatchUpdateCurrentOrganization(organization: UpdateOrganizationDto): void {
        this.store.dispatch(fromActions.updateCurrentOrganization.do({ payload: organization }));
    }

    setCurrentOrganization(user: Partial<GetOrganizationDto>): void {
        this.store.dispatch(fromActions.setCurrentOrganization({ payload: user }));
    }

    resetCurrentOrganization(): void {
        this.store.dispatch(fromActions.resetCurrentOrganization());
    }

    dispatchGetOrganizations(): void {
        this.store.dispatch(fromActions.getAllOrganizations.do());
    }

    dispatchSearchOrganizations(filter: OrganizationSearchFilterDto): void {
        this.store.dispatch(fromActions.searchOrganizations.search({ payload: filter }));
    }

    dispatchCreateIndex(): void {
        this.store.dispatch(fromActions.createIndexAction.do());
    }

    getAllOrganizations$(): Observable<GetOrganizationDto[] | undefined> {
        return this.store.pipe(select(fromFeature.selectAllOrganizations));
    }
    getSearchResults$(): Observable<OrganizationSearchResultDto[] | undefined> {
        return this.store.pipe(select(fromFeature.selectSearchResults));
    }

    dispatchUpdateOrganizationState(updateOrganizationState: IUpdateOrganizationState): void {
        return this.store.dispatch(fromActions.updateOrganizationState.do({ payload: updateOrganizationState }));
    }

    dispatchUpdateOrganizationTier(updateOrganizationTier: UpdateOrganizationTierDto): void {
        return this.store.dispatch(fromActions.updateOrganizationTier.do({ payload: updateOrganizationTier }));
    } 
    
    dispatchUpdateOrganizationSubscription(updateOrganizationTier: UpdateOrganizationTierDto): void {
        return this.store.dispatch(fromActions.updateOrganizationTier.forownorg({ payload: updateOrganizationTier }));
    }

    dispatchUpdateOrganizationAbcp(updateOrganizationAbcp: UpdateOrganizationAbcpDto): void {
        return this.store.dispatch(fromActions.updateOrganizationAbcp.do({ payload: updateOrganizationAbcp }));
    }

    dispatchUpdateOrganizationLocale(updateOrganizationLocale: UpdateOrganizationLocaleDto): void {
        return this.store.dispatch(fromActions.updateOrganizationLocale.do({ payload: updateOrganizationLocale }));
    }

    dispatchUpdateOrganizationSettings(organizationSettings: OrganizationSettings): void {
        return this.store.dispatch(fromActions.updateOrganizationSettings.do({ payload: organizationSettings }));
    }
    dispatchGetOrganizationSettings(): void {
        return this.store.dispatch(fromActions.getOrganizationSettings.do());
    }
    getOrganizationSettings(): Observable<OrganizationSettings> {
        return this.store.pipe(select(fromFeature.selectCurrentOrganizationSettings));
    }
    dispatchGetUsers(includeDeleted: boolean = false): void {
        this.store.dispatch(fromActions.getUsers.do({ includeDeleted }));
    }

    getUsers(): Observable<GetUserDto[]> {
        return this.store.select(fromFeature.selectAllUsers);
    }

    getPossibleAssignees(): Observable<GetUserDto[]> {
        return this.store.select(fromFeature.selectAllPossibleAssignees);
    }

    dispatchInviteUsers(userEmails: string[]): void {
        this.store.dispatch(fromActions.inviteUsers.do({ payload: userEmails }));
    }
    dispatchRemoveUsers(userIds: string[]): void {
        this.store.dispatch(fromActions.removeUsers.do({ payload: userIds }));
    }

    dispatchUploadOrganizationOrReportingTemplate(
        uploadOrganizationReportingTemplate: IUploadOrganizationReportingTemplateDto,
    ): void {
        return this.store.dispatch(
            fromActions.uploadOrganizationOrReportingTemplate.do({ payload: uploadOrganizationReportingTemplate }),
        );
    }

    dispatchUploadOrganizationHgbReportingTemplate(
        uploadOrganizationReportingTemplate: IUploadOrganizationReportingTemplateDto,
    ): void {
        return this.store.dispatch(
            fromActions.uploadOrganizationHgbReportingTemplate.do({ payload: uploadOrganizationReportingTemplate }),
        );
    }

    dispatchInviteReferrals(referrals: referralDto): void {
        this.store.dispatch(fromActions.inviteReferrals.do({ payload: referrals }));
    }

    dispatchAcceptInviteReferrals(userId: string): void {
        this.store.dispatch(fromActions.inviteReferrals.accept({ payload: userId }));
    }

    dispatchSetOrganizationReportingTemplateToDefault(organizationId: string): void {
        return this.store.dispatch(
            fromActions.setOrganizationReportingTemplateToDefault.do({ payload: organizationId }),
        );
    }

    dispatchSetOrganizationHgbReportingTemplateToDefault(organizationId: string): void {
        return this.store.dispatch(
            fromActions.setOrganizationHgbReportingTemplateToDefault.do({ payload: organizationId }),
        );
    }

    dispatchUploadOrganizationOrExcelTemplate(
        uploadOrganizationExcelTemplate: IUploadOrganizationReportingTemplateDto,
    ): void {
        return this.store.dispatch(
            fromActions.uploadOrganizationOrExcelTemplate.do({ payload: uploadOrganizationExcelTemplate }),
        );
    }

    dispatchUploadOrganizationHgbExcelTemplate(
        uploadOrganizationExcelTemplate: IUploadOrganizationReportingTemplateDto,
    ): void {
        return this.store.dispatch(
            fromActions.uploadOrganizationHgbExcelTemplate.do({ payload: uploadOrganizationExcelTemplate }),
        );
    }

    dispatchSetOrganizationExcelTemplateToDefault(organizationId: string): void {
        return this.store.dispatch(
            fromActions.setOrganizationExcelTemplateToDefault.do({ payload: organizationId }),
        );
    }

    dispatchSetOrganizationHgbExcelTemplateToDefault(organizationId: string): void {
        return this.store.dispatch(
            fromActions.setOrganizationHgbExcelTemplateToDefault.do({ payload: organizationId }),
        );
    }

    dispatchDeactivateFCP(surveyData: string): void {
        return this.store.dispatch(fromActions.fcpManagement.deactivateFcp({ surveyData }));
    }

    dispatchAskForFCPTrial(): void {
        return this.store.dispatch(fromActions.fcpManagement.askForFcpApproval());
    }

    dispatchChangeFCPStatus(organizationId: string, fcpState: FCPState): void {
        return this.store.dispatch(fromActions.fcpManagement.changeStateFcp({ organizationId, fcpState }));
    }

    dispatchChangeCPMMState(organizationId: string, cpmmState: CPMMState): void {
        return this.store.dispatch(fromActions.cpmmActivation.changeCpmmState({ organizationId, cpmmState }));
    }

    dispatchWithdrawInvite(invitationId: string[]): void {
        this.store.dispatch(fromActions.inviteUsers.withdraw({ payload: invitationId }));
    }

    dispatchResendInvitation(invitationId: string): void {
        this.store.dispatch(fromActions.inviteUsers.resendinvitation({ payload: invitationId }));
    }

    dispatchGetLicenceStatus(): void {
        this.store.dispatch(fromLicenceActions.licenceActions.getLicenceStatus());
    }

    getLicenceStatus$(): Observable<LicenceStatusDto> {
        return this.store.select(fromFeature.selectLicenceStatus);
    }

    dispatchRequestMoreLicences() {
        this.store.dispatch(fromLicenceActions.licenceActions.requestUpdateLicence());
    }

    cpmmIsActive$(): Observable<boolean> {
        return this.store.select(fromFeature.selectIsCpmmActive);
    }

    dispatchUpdateUserRole(payload: { userId: string; role: string }): void {
        this.store.dispatch(fromActions.updateUserRole.do({ payload }));
    }

    dispatchBulkUpdateUserRole(payload: { userIds: string[]; role: string }): void {
        this.store.dispatch(fromActions.bulkUpdateUserRole.do({ payload }));
    }
}
